import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";

export default function StudentAccount() {
  const navigate = useNavigate();
  const [lastSubmission, setLastSubmission] = useState(null);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  const onSubmit = (data) => {
    setLastSubmission(data);
    toast.success("Preview only: student account registration is currently a mock form.");
    setTimeout(() => {
      navigate("/instruments-levels");
    }, 900);
    reset();
  };

  return (
    <div className="space-y-8">
      <section className="space-y-4">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Student Account &amp; Registration</h1>
        <p className="max-w-3xl text-sm leading-relaxed text-slate-700 sm:text-base">
          The student account system is designed to help candidates and guardians manage examination entries, track results and access certificates. The preview form below illustrates the type of information that may be requested during registration.
        </p>
        <p className="text-xs text-slate-500">
          For the purposes of this website preview, the registration form operates as a front-end mock only. No data is stored or transmitted.
        </p>
      </section>

      <section className="grid gap-6 md:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <Card className="border-slate-200 bg-white/80 shadow-sm backdrop-blur-xl">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-slate-900">
              Create a student profile (preview)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 text-sm">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="fullName">Student name</Label>
                  <Input
                    id="fullName"
                    placeholder="Full name"
                    {...register("fullName", { required: "Please enter the student name." })}
                  />
                  {errors.fullName && (
                    <p className="text-xs text-red-600">{errors.fullName.message}</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="email">Email address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="name@example.com"
                    {...register("email", { required: "Please provide an email address." })}
                  />
                  {errors.email && (
                    <p className="text-xs text-red-600">{errors.email.message}</p>
                  )}
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="primaryInstrument">Primary instrument</Label>
                  <Select
                    onValueChange={(value) => {
                      // integrate with react-hook-form manually
                      const event = { target: { name: "instrument", value } };
                      register("instrument").onChange(event);
                    }}
                  >
                    <SelectTrigger id="primaryInstrument">
                      <SelectValue placeholder="Select instrument" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="piano">Piano</SelectItem>
                      <SelectItem value="violin">Violin</SelectItem>
                      <SelectItem value="guitar">Guitar</SelectItem>
                      <SelectItem value="voice">Voice</SelectItem>
                      <SelectItem value="drums">Drum Kit</SelectItem>
                      <SelectItem value="alto-saxophone">Alto Saxophone</SelectItem>
                      <SelectItem value="voice-over">Voice Over</SelectItem>
                      <SelectItem value="music-therapy">Music Therapy</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.instrument && (
                    <p className="text-xs text-red-600">{errors.instrument.message}</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="targetLevel">Intended examination level</Label>
                  <Select
                    onValueChange={(value) => {
                      const event = { target: { name: "level", value } };
                      register("level").onChange(event);
                    }}
                  >
                    <SelectTrigger id="targetLevel">
                      <SelectValue placeholder="Select level" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Level 1</SelectItem>
                      <SelectItem value="2">Level 2</SelectItem>
                      <SelectItem value="3">Level 3</SelectItem>
                      <SelectItem value="4">Level 4</SelectItem>
                      <SelectItem value="5">Level 5</SelectItem>
                      <SelectItem value="6">Level 6</SelectItem>
                      <SelectItem value="ai-voiceover">AI Voiceover Master Certification</SelectItem>
                      <SelectItem value="music-therapy">Music Therapy Professional Certification</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.level && (
                    <p className="text-xs text-red-600">{errors.level.message}</p>
                  )}
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="guardianName">Parent / guardian (if applicable)</Label>
                <Input
                  id="guardianName"
                  placeholder="Name of parent or guardian"
                  {...register("guardianName")}
                />
              </div>

              <Button
                type="submit"
                className="mt-2 w-full bg-sky-700 text-sm font-semibold text-white hover:bg-sky-800"
              >
                Submit preview registration
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card className="border-slate-200 bg-slate-50/80 shadow-sm backdrop-blur-xl">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-slate-900">
              Account features
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-slate-700">
            <p>
              A full student account will typically allow candidates and guardians to:
            </p>
            <ul className="space-y-1">
              <li>• Register for upcoming examination sessions and select preferred modes.</li>
              <li>• View confirmed examination details and guidance for the examination day.</li>
              <li>• Access results, feedback summaries and digital certificates.</li>
            </ul>

            {lastSubmission && (
              <div className="mt-4 rounded-md border border-sky-100 bg-white/80 p-3 text-xs text-slate-700">
                <p className="mb-1 font-semibold text-slate-900">Most recent preview data</p>
                <p>Name: {lastSubmission.fullName}</p>
                <p>Email: {lastSubmission.email}</p>
                {lastSubmission.instrument && <p>Instrument: {lastSubmission.instrument}</p>}
                {lastSubmission.level && <p>Intended level: {lastSubmission.level}</p>}
              </div>
            )}
          </CardContent>
        </Card>
      </section>
    </div>
  );
}
